/**
 * Backend-neutral access to the rendered frame's pixels.
 *
 * WebGL: callers keep using gl.readPixels on the default framebuffer inside
 * 'postrender' (synchronous, proven on iPhone Safari + Chrome — see
 * capture-frame.ts and the paint probe in mount.ts).
 *
 * WebGPU: there is no readPixels on the presented canvas, and drawImage() of a
 * WebGPU canvas came back black in every configuration we tried (Chrome 141,
 * headless and paned). What IS readable is the CameraFrame's intermediate
 * scene target — the RGBA16F texture every splat is composed into before the
 * tonemap/compose pass writes the backbuffer. `Texture.read` copies a sub-rect
 * of it to a staging buffer and maps it (async, one frame of latency). The
 * values are linear HDR, so they are converted to the same 8-bit sRGB scale the
 * WebGL paths produce and the probe thresholds were calibrated on.
 *
 * `renderPassCamera` / `sceneTexture` / `rt` are CameraFrame internals, not in
 * the public typings; verified on engine 2.22.1. Every consumer degrades to a
 * time-based fallback when they are missing, so an engine bump that renames
 * them costs a slower reveal, never a broken viewer.
 */

import type * as pc from 'playcanvas';

const frames = new WeakMap<pc.AppBase, pc.CameraFrame>();
// an alternative scene target (the editor-style pipeline's 16F colour buffer); wins over the CameraFrame
const providers = new WeakMap<pc.AppBase, () => SceneTarget | null>();

export function registerSceneTarget(app: pc.AppBase, provider: () => SceneTarget | null): void {
    providers.set(app, provider);
}

/** Called once by ViewerApp so the probes can find the scene target. */
export function registerCameraFrame(app: pc.AppBase, cameraFrame: pc.CameraFrame): void {
    frames.set(app, cameraFrame);
}

export function isWebGpu(app: pc.AppBase): boolean {
    return app.graphicsDevice.deviceType === 'webgpu';
}

export interface SceneTarget {
    texture: pc.Texture;
    rt: pc.RenderTarget;
    /** true when the target already holds display-encoded (sRGB) values, not linear HDR */
    encoded?: boolean;
}

/** The CameraFrame's scene colour target, or null when not (yet) available. */
export function sceneTargetOf(app: pc.AppBase): SceneTarget | null {
    const provided = providers.get(app)?.();
    if (provided) return provided;
    const cf = frames.get(app);
    if (!cf) return null;
    const rpc = (cf as unknown as {
        renderPassCamera?: { sceneTexture?: pc.Texture; rt?: pc.RenderTarget };
    }).renderPassCamera;
    const texture = rpc?.sceneTexture;
    const rt = rpc?.rt;
    return texture && rt ? { texture, rt } : null;
}

// IEEE 754 binary16 → number. Scene targets are RGBA16F on every device that
// supports it (see app.ts renderFormats), so this is the hot path.
export function halfToFloat(h: number): number {
    const s = (h & 0x8000) ? -1 : 1;
    const e = (h >> 10) & 0x1f;
    const m = h & 0x3ff;
    if (e === 0) return s * m * 2 ** -24;                 // subnormal
    if (e === 31) return m ? NaN : s * Infinity;
    return s * (1 + m / 1024) * 2 ** (e - 15);
}

/** Linear [0,∞) → sRGB byte. Same transfer the compose pass applies for the backbuffer. */
export function linearToSrgb8(v: number): number {
    if (!(v > 0)) return 0;
    if (v >= 1) return 255;
    const s = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.round(s * 255);
}

// Engine pixel-format ids (2.22.1 constants), inlined so this module stays a
// type-only import of 'playcanvas' and capture/ keeps its zero runtime weight.
const PIXELFORMAT_RGBA8 = 7;
const PIXELFORMAT_RGBA16F = 12;
const PIXELFORMAT_RGBA32F = 14;

/**
 * Read a rect of the scene target as 8-bit sRGB RGBA with alpha pinned to 255.
 * Row order is the texture's (top row first on WebGPU — verified against a
 * page screenshot). Resolves after the GPU copy maps; call from wherever a
 * one-frame-old answer is fine (the probe, a capture).
 */
export async function readSceneRect(
    app: pc.AppBase,
    x: number,
    y: number,
    w: number,
    h: number
): Promise<Uint8ClampedArray<ArrayBuffer>> {
    const st = sceneTargetOf(app);
    if (!st) throw new Error('scene target unavailable');
    // Refuse before the GPU sees it. On WebGPU a copy that touches outside
    // the texture does not just fail: it invalidates the frame's whole
    // command buffer, so NOTHING of that frame reaches the screen. The paint
    // probe did exactly that on the iPhone 17 Pro behind the stand demo's
    // quarter-resolution loader render (a 98 px wide target, 64 px rows at
    // 70% across): every tenth frame black, the probe never fed, the loader
    // never let go (14/10/2026).
    const tw = st.texture.width;
    const th = st.texture.height;
    if (x < 0 || y < 0 || w < 1 || h < 1 || x + w > tw || y + h > th) {
        throw new Error(`scene rect ${x},${y} ${w}x${h} outside target ${tw}x${th}`);
    }
    const raw = await (st.texture as unknown as {
        read(x: number, y: number, w: number, h: number, o: { renderTarget: pc.RenderTarget }): Promise<ArrayBufferView>;
    }).read(x, y, w, h, { renderTarget: st.rt });
    const n = w * h;
    const out = new Uint8ClampedArray(n * 4);
    const format = st.texture.format;
    if (format === PIXELFORMAT_RGBA16F && st.encoded) {
        // display-encoded already (editor-style pipeline): just quantise
        const src = raw as Uint16Array;
        const q = (h: number) => Math.max(0, Math.min(255, Math.round(halfToFloat(h) * 255)));
        for (let i = 0; i < n; i++) {
            out[i * 4] = q(src[i * 4]);
            out[i * 4 + 1] = q(src[i * 4 + 1]);
            out[i * 4 + 2] = q(src[i * 4 + 2]);
            out[i * 4 + 3] = 255;
        }
    } else if (format === PIXELFORMAT_RGBA16F) {
        const src = raw as Uint16Array;
        for (let i = 0; i < n; i++) {
            out[i * 4] = linearToSrgb8(halfToFloat(src[i * 4]));
            out[i * 4 + 1] = linearToSrgb8(halfToFloat(src[i * 4 + 1]));
            out[i * 4 + 2] = linearToSrgb8(halfToFloat(src[i * 4 + 2]));
            out[i * 4 + 3] = 255;
        }
    } else if (format === PIXELFORMAT_RGBA32F) {
        const src = raw as Float32Array;
        for (let i = 0; i < n; i++) {
            out[i * 4] = linearToSrgb8(src[i * 4]);
            out[i * 4 + 1] = linearToSrgb8(src[i * 4 + 1]);
            out[i * 4 + 2] = linearToSrgb8(src[i * 4 + 2]);
            out[i * 4 + 3] = 255;
        }
    } else if (format === PIXELFORMAT_RGBA8) {
        const src = raw as Uint8Array;
        for (let i = 0; i < n; i++) {
            out[i * 4] = src[i * 4];
            out[i * 4 + 1] = src[i * 4 + 1];
            out[i * 4 + 2] = src[i * 4 + 2];
            out[i * 4 + 3] = 255;
        }
    } else {
        // 11-11-10 packed float and friends: not worth decoding for a probe.
        throw new Error(`scene target format ${format} not readable`);
    }
    return out;
}
